import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getUserListApi } from '~/features/user/api/get-user-list.api';
import { getUserApi } from '~/features/user/api/get-user.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type { User, UserListParams } from '~/features/user/types/user.type';

export interface UserState {
  isLoading: boolean;
  list: User[];
  meta: PaginationMeta | null;
  params: UserListParams | null;
  selectedUser: User | null;
}

const initialState: UserState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedUser: null,
};

/**
 * 사용자 목록 조회
 */
export const fetchUserList = createAsyncThunk<
  PaginatedApiResponse<User>,
  UserListParams | undefined
>('user/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getUserListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * 사용자 상세 조회
 */
export const fetchUserDetail = createAsyncThunk<User, number>(
  'user/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getUserApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const UserSlice = createSlice({
  initialState,
  name: 'user',
  reducers: {
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    resetSelectedUser: state => {
      state.selectedUser = null;
    },
    resetUser: state => {
      state.selectedUser = null;
      state.list = [];
      state.meta = null;
      state.params = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // fetchUserList
      .addCase(fetchUserList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchUserList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchUserList.rejected, state => {
        state.isLoading = false;
      })
      // fetchUserDetail
      .addCase(fetchUserDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchUserDetail.fulfilled, (state, action) => {
        state.selectedUser = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchUserDetail.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const { setSelectedUser, resetSelectedUser, resetUser } = UserSlice.actions;
export default UserSlice;
